import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { withStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import AccountCircle from '@material-ui/icons/AccountCircle'
import CalendarToday from '@material-ui/icons/CalendarToday'
import Phone from '@material-ui/icons/Phone'
import EmailIcon from '@material-ui/icons/Email'
import Place from '@material-ui/icons/Place'
import LocationCity from '@material-ui/icons/LocationCity'
import MaskedInput from 'react-text-mask'
import { withTranslate } from 'react-redux-multilingual'
import States from '../modules/states'
import Cities from '../modules/cities'
import FormTextField from './booking-info/FormTextField'
import FormSelect from './booking-info/FormSelect'
import { BRAZIL_COUNTRY_ID } from '../helpers/checkin-validators'
import { isValidCPF } from '../helpers/cpf'

const CPF_MASK = [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/]

// Campos cuja validação segue o padrão "required && !valid && blank".
// Renderizados em sequência via .map() — cada um vira um <FormTextField>.
// Todos os campos usam apenas `gridItemClasses` (padding 15/15) no outer.
// `inputField` (marginTop:15) só existe no surname, onde é estrutural:
// surname não tem padding outer porque vive no inner-grid com name, e
// precisa de margin pra alinhar com o name (que tem padding-top via 'pad').
// Não usar `inputField` em mais nenhum campo — isso causa desalinhamento
// quando a visibilidade dinâmica remonta os pares horizontais.
const SIMPLE_TEXT_FIELDS = [
  { field: 'name',         valueKey: 'name',         id: 'firstName',    labelKey: 'firstNameLabel',    helperKey: 'firstNameHelperText',    iconKey: 'account', autoComplete: 'fname',        maxLength: 50, autoFocus: true, outer: 'pad' },
  { field: 'surname',      valueKey: 'surname',      id: 'lastName',     labelKey: 'lastNameLabel',     helperKey: 'lastNameHelperText',     iconKey: 'account', autoComplete: 'lname',        maxLength: 50, outer: 'none', inner: 'inputField' },
  { field: 'phone',        valueKey: 'phone',        id: 'phone',        labelKey: 'telephoneLabel',    helperKey: 'telephoneHelperText',    iconKey: 'phone',   autoComplete: 'telf',         maxLength: 20 },
  { field: 'address',      valueKey: 'address',      id: 'address',      labelKey: 'addressLabel',      helperKey: 'addressHelperText',      iconKey: 'city',    autoComplete: 'address',      maxLength: 50 },
  { field: 'number',       valueKey: 'number',       id: 'number',       labelKey: 'numberLabel',       helperKey: 'numberHelperText',       iconKey: 'city',    autoComplete: 'number',       maxLength: 5  },
  { field: 'complement',   valueKey: 'complement',   id: 'complement',   labelKey: 'complementLabel',   helperKey: 'complementHelperText',   iconKey: 'city',    autoComplete: 'complement',   maxLength: 5  },
  { field: 'neighborhood', valueKey: 'neighborhood', id: 'neighborhood', labelKey: 'neighborhoodLabel', helperKey: 'neighborhoodHelperText', iconKey: 'city',    autoComplete: 'neighborhood', maxLength: 5  },
  { field: 'post_code',    valueKey: 'postal_code',  id: 'postalCode',   labelKey: 'postalCodeLabel',   helperKey: 'postalCodeHelperText',   iconKey: 'place',   autoComplete: 'postalCode',   maxLength: 50 }
]

const ICONS = {
  account: <AccountCircle />,
  phone: <Phone />,
  city: <LocationCity />,
  place: <Place />,
  email: <EmailIcon />,
  calendar: <CalendarToday />
}

class BookingInfoBasic extends React.Component {
  state = {
    stateList: [],
    cityList: []
  }

  constructor(props) {
    super(props)
    this.onFocus = this.onFocus.bind(this)
    this.DateFormatCustom = this.DateFormatCustom.bind(this)
    this.CPFFormatCustom = this.CPFFormatCustom.bind(this)
    this.birthDateRef = (ref) => { this.birthdate = ref }
  }

  componentDidMount() {
    this.loadSelectedCEP()
  }

  getFieldConfig = (fieldName) =>
    this.props.checkinFieldsBehaviour.find((e) => e.field === fieldName) || {}

  handleSelectedGender = (event) => {
    this.props.setCheckinField('gender', event.target.value)
  }

  // Nacionalidade não dispara fetch de estados/cidades — é apenas declarativa.
  // Dirige a regra de CPF (validado só se nacionalidade for Brasil).
  handleSelectedNationality = (event) => {
    this.props.setCheckinField('nationality', event.target.value)
  }

  handleSelectedCity = (event) => {
    this.props.setCheckinField('city', event.target.value)
  }

  countryChangeHandler = async (event) => {
    const selectedCountry = event.target.value
    this.props.setCheckinField('country', selectedCountry)
    this.setState({ stateList: [], cityList: [] })

    const countryStates = await States.getCountryStates(selectedCountry)
    if (countryStates) {
      this.setState({ stateList: countryStates.result.states })
    }
  }

  countryStateChangeHandler = async (event) => {
    const selectedState = event.target.value
    this.props.setCheckinField('state', selectedState)
    this.setState({ cityList: [] })

    const cities = await Cities.getCities(selectedState)
    if (cities) {
      this.setState({ cityList: cities.result.cities })
    }
  }

  loadSelectedCEP = async () => {
    if (this.props.checkin.country > 0) {
      const countryStates = await States.getCountryStates(this.props.checkin.country)
      if (countryStates) {
        this.setState({ stateList: countryStates.result.states })
      }
    }
    if (this.props.checkin.state > 0) {
      const cities = await Cities.getCities(this.props.checkin.state)
      if (cities) {
        this.setState({ cityList: cities.result.cities })
      }
    }
  }

  renderSimpleField = (spec) => {
    const { classes, translate, checkin, valid, handleChangeTF } = this.props
    const config = this.getFieldConfig(spec.field)
    const value = checkin[spec.valueKey]
    const blank = !value || value === ''

    return (
      <FormTextField
        key={spec.field}
        visible={config.visible}
        required={config.required}
        error={config.required && valid === false && blank}
        errorText={translate(spec.helperKey)}
        id={spec.id}
        name={spec.id}
        label={translate(spec.labelKey)}
        value={value}
        onChange={handleChangeTF(spec.valueKey)}
        icon={ICONS[spec.iconKey]}
        autoComplete={spec.autoComplete}
        maxLength={spec.maxLength}
        autoFocus={spec.autoFocus}
        outerClassName={
          spec.outer === 'pad' ? classes.gridItemClassesPad
          : spec.outer === 'none' ? undefined
          : classes.gridItemClasses
        }
        innerClassName={spec.inner === 'inputField' ? classes.inputField : undefined}
      />
    )
  }

  render() {
    const { classes, translate, checkin, valid, validEmail, validDocument, validDateBirth, handleChangeTF, genders, countries } = this.props
    const f = this.getFieldConfig

    // Brasileiro? Direciona se CPF é obrigatório, se a máscara aparece,
    // e qual mensagem de erro mostrar (vazio vs. checksum inválido).
    // Usa nationality (não o country do endereço) — um brasileiro morando
    // no exterior continua com CPF obrigatório.
    // Formato (checksum) é cobrado sempre que há valor — required só governa
    // a regra de blank.
    const isBrazilian = Number(checkin.nationality) === BRAZIL_COUNTRY_ID
    const taxIdConfigRequired = f('tax_id').required
    const taxIdRequired = taxIdConfigRequired && isBrazilian
    const taxIdBlank = !checkin.tax_id
    const taxIdInvalidFormat = isBrazilian && !taxIdBlank && !isValidCPF(checkin.tax_id)
    const taxIdError =
      valid === false && ((taxIdRequired && taxIdBlank) || taxIdInvalidFormat)
    const taxIdErrorText = taxIdBlank
      ? translate('taxIdHelperText')
      : translate('taxIdInvalidHelperText')

    return (
      <Grid container>
        <Grid container className={classes.gridItemClasses}>
          {this.renderSimpleField(SIMPLE_TEXT_FIELDS[0])}
          {this.renderSimpleField(SIMPLE_TEXT_FIELDS[1])}
        </Grid>

        <FormTextField
          visible={f('email').visible}
          required={f('email').required}
          error={f('email').required && validEmail}
          errorText={translate('emailHelperText')}
          id="email"
          name="email"
          label={translate('emailLabel')}
          value={checkin.email}
          onChange={handleChangeTF('email')}
          icon={ICONS.email}
          autoComplete="email"
          maxLength={50}
          outerClassName={classes.gridItemClasses}
        />

        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[2])}

        <FormSelect
          visible={f('country').visible}
          required={f('country').required}
          error={f('country').required && valid === false && checkin.nationality < 0}
          errorText={translate('nationalityHelperText')}
          id="nationality-required"
          name="nationality"
          label={translate('nationalityLabel')}
          value={checkin.nationality}
          onChange={this.handleSelectedNationality}
          options={countries.map((c) => ({ value: c.id, label: c.name }))}
          native
          outerClassName={classes.gridItemClasses}
        />

        <FormTextField
          visible={f('document').visible}
          required={f('document').required}
          error={f('document').required && validDocument}
          errorText={translate('documentHelperText')}
          id="iddoc"
          name="iddoc"
          label={translate('documentLabel')}
          value={checkin.document}
          onChange={handleChangeTF('document')}
          icon={ICONS.account}
          autoComplete="iddoc"
          maxLength={20}
          outerClassName={classes.gridItemClasses}
        />

        <FormTextField
          visible={f('tax_id').visible}
          required={taxIdRequired}
          error={taxIdError}
          errorText={taxIdErrorText}
          id="tax_id"
          name="tax_id"
          label={translate('taxIdLabel')}
          value={checkin.tax_id}
          onChange={handleChangeTF('tax_id')}
          icon={ICONS.account}
          autoComplete="tax_id"
          maxLength={20}
          inputComponent={isBrazilian ? this.CPFFormatCustom : undefined}
          outerClassName={classes.gridItemClasses}
        />

        <FormTextField
          visible={f('date_of_birth').visible}
          required={f('date_of_birth').required}
          error={f('date_of_birth').required && validDateBirth}
          errorText={translate('dateinputHelperText')}
          id="dateinput"
          name="dateinput"
          label={translate('dateinputLabel')}
          value={checkin.birth_date}
          onChange={handleChangeTF('birth_date')}
          icon={ICONS.calendar}
          autoComplete="dateinput"
          placeholder="dd/MM/yyyy"
          inputComponent={this.DateFormatCustom}
          inputRef={this.birthDateRef}
          onFocus={this.onFocus}
          outerClassName={classes.gridItemClasses}
        />

        <FormSelect
          visible={f('gender').visible}
          required={f('gender').required}
          error={f('gender').required && valid === false && checkin.gender < 0}
          errorText={translate('genderHelperText')}
          id="gender-required"
          name="gender"
          label={translate('genderLabel')}
          value={checkin.gender}
          onChange={this.handleSelectedGender}
          options={genders.map((g) => ({ value: g.id, label: g.name }))}
          native
          outerClassName={classes.gridItemClasses}
        />

        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[3])}
        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[4])}
        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[5])}
        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[6])}
        {this.renderSimpleField(SIMPLE_TEXT_FIELDS[7])}

        <FormSelect
          visible={f('country').visible}
          required={f('country').required}
          error={f('country').required && valid === false && checkin.country < 0}
          errorText={translate('countryHelperText')}
          id="country-required"
          name="country"
          label={translate('countryLabel')}
          value={checkin.country}
          onChange={this.countryChangeHandler}
          options={countries.map((c) => ({ value: c.id, label: c.name }))}
          native
          outerClassName={classes.gridItemClasses}
        />

        <FormSelect
          visible={f('state').visible}
          required={f('state').required}
          error={f('state').required && valid === false && checkin.state < 0}
          errorText={translate('stateHelperText')}
          id="state-required"
          name="state"
          label={translate('stateLabel')}
          value={checkin.state}
          onChange={this.countryStateChangeHandler}
          options={this.state.stateList.map((s) => ({ value: s.id, label: s.name }))}
          native
          outerClassName={classes.gridItemClasses}
        />

        <FormSelect
          visible={f('city').visible}
          required={f('city').required}
          error={f('city').required && valid === false && checkin.city < 0}
          errorText={translate('cityHelperText')}
          id="city-required"
          name="city"
          label={translate('cityLabel')}
          value={checkin.city}
          onChange={this.handleSelectedCity}
          options={this.state.cityList.map((c) => ({ value: c.id, label: c.name }))}
          native
          outerClassName={classes.gridItemClasses}
        />
      </Grid>
    )
  }

  onFocus() {
    if (this.props.checkin.birth_date === '' && this.birthdate) {
      this.birthdate.selectionStart = 0
      this.birthdate.selectionEnd = 0
    }
  }

  DateFormatCustom(props) {
    const { inputRef, ...other } = props
    return (
      <MaskedInput
        {...other}
        mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
        ref={(ref) => {
          inputRef(ref ? ref.inputElement : null)
        }}
        placeholder={this.props.translate('dateformat')}
        placeholderChar={' '}
      />
    )
  }

  CPFFormatCustom(props) {
    const { inputRef, ...other } = props
    return (
      <MaskedInput
        {...other}
        mask={CPF_MASK}
        ref={(ref) => {
          inputRef(ref ? ref.inputElement : null)
        }}
        placeholderChar={' '}
      />
    )
  }
}

const styles = (theme) => ({
  inputField: {
    marginTop: 15
  },
  gridItemClassesPad: {
    paddingTop: 15
  },
  gridItemClasses: {
    paddingTop: 15,
    paddingBottom: 15
  }
})

BookingInfoBasic.propTypes = {
  classes: PropTypes.object.isRequired
}

export default compose(
  withStyles(styles),
  connect()
)(withTranslate(BookingInfoBasic))
